import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import '../css/KlusPlanning.css';

const API_URL = '';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Nog niet' },
  { value: 'uitgevoerd', label: 'Uitgevoerd' },
  { value: 'deels', label: 'Deels' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const KlusPlanning = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null); // 'notfound' | 'expired' | 'generic'
  // Per-booking save status: { [bookingId]: 'saving' | 'saved' | 'error' }
  const [saveState, setSaveState] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/planning/klus/${token}`);
      if (res.status === 404) { setErrorState('notfound'); return; }
      if (res.status === 410) { setErrorState('expired'); return; }
      if (!res.ok) { setErrorState('generic'); return; }
      setData(await res.json());
    } catch (err) {
      setErrorState('generic');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Update a booking field locally
  const updateLocal = (slotId, bookingId, patch) => {
    setData(prev => ({
      ...prev,
      slots: prev.slots.map(s => s.slotId !== slotId ? s : {
        ...s,
        bookings: s.bookings.map(b => b._id !== bookingId ? b : { ...b, ...patch })
      })
    }));
  };

  const save = async (bookingId, patch) => {
    setSaveState(prev => ({ ...prev, [bookingId]: 'saving' }));
    try {
      const res = await fetch(`${API_URL}/api/planning/klus/${token}/booking/${bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.status === 410) { setErrorState('expired'); return; }
      if (!res.ok) throw new Error('save failed');
      setSaveState(prev => ({ ...prev, [bookingId]: 'saved' }));
      setTimeout(() => {
        setSaveState(prev => {
          if (prev[bookingId] !== 'saved') return prev;
          const { [bookingId]: _, ...rest } = prev;
          return rest;
        });
      }, 2000);
    } catch (err) {
      setSaveState(prev => ({ ...prev, [bookingId]: 'error' }));
    }
  };

  const handleStatus = (slotId, booking, value) => {
    // Bij 'uitgevoerd'/'deels' automatisch datum van vandaag invullen als nog leeg
    const patch = { uitvoerStatus: value };
    if (value !== 'open' && !booking.uitvoerDatum) patch.uitvoerDatum = todayISO();
    updateLocal(slotId, booking._id, patch);
    save(booking._id, patch);
  };

  if (loading) {
    return <div className="klus-page"><div className="klus-container"><p>Laden...</p></div></div>;
  }

  if (errorState) {
    const msg = {
      notfound: 'Deze link bestaat niet (meer).',
      expired: 'Deze link is verlopen. Vraag het bestuur om een nieuwe link.',
      generic: 'Er ging iets mis bij het laden. Probeer het later opnieuw.',
    }[errorState];
    return (
      <div className="klus-page">
        <div className="klus-container">
          <div className="klus-header">
            <img src={LogoPolderzicht} alt="VVE Polderzicht" className="klus-logo" />
          </div>
          <div className="klus-alert error">{msg}</div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const verlooptOp = data.verlooptOp ? new Date(data.verlooptOp).toLocaleDateString('nl-NL') : null;
  const totaal = data.slots.reduce((n, s) => n + s.bookings.length, 0);
  const gedaan = data.slots.reduce((n, s) => n + s.bookings.filter(b => b.uitvoerStatus === 'uitgevoerd').length, 0);

  return (
    <div className="klus-page">
      <div className="klus-container">
        <div className="klus-header">
          <img src={LogoPolderzicht} alt="VVE Polderzicht" className="klus-logo" />
          <h1>{data.titel?.nl || 'Uitvoering'}</h1>
          <p className="klus-sub">Uitvoeringslijst &middot; {gedaan} van {totaal} uitgevoerd</p>
          {verlooptOp && <p className="klus-expiry">Link geldig t/m {verlooptOp}</p>}
        </div>

        {data.slots.map(slot => (
          <div key={slot.slotId} className="klus-slot">
            <h2>{slot.label?.nl || slot.slotId}</h2>
            {slot.tijd && <p className="klus-slot-tijd">{slot.tijd}</p>}

            {slot.bookings.length === 0 ? (
              <p className="klus-empty">Geen inschrijvingen voor dit tijdslot.</p>
            ) : (
              slot.bookings.map(b => (
                <div key={b._id} className={`klus-row status-${b.uitvoerStatus}`}>
                  <div className="klus-row-head">
                    <div className="klus-huis">
                      <span className="klus-huisnr">Huisnr {b.huisnummer}</span>
                      <span className="klus-naam">{b.naam}</span>
                      {b.telefoon && <a className="klus-tel" href={`tel:${b.telefoon}`}>{b.telefoon}</a>}
                    </div>
                    {saveState[b._id] === 'saving' && <span className="klus-save saving">Opslaan…</span>}
                    {saveState[b._id] === 'saved' && <span className="klus-save saved">Opgeslagen ✓</span>}
                    {saveState[b._id] === 'error' && <span className="klus-save error">Mislukt</span>}
                  </div>

                  <div className="klus-status-btns">
                    {STATUS_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`klus-status-btn ${b.uitvoerStatus === opt.value ? 'active ' + opt.value : ''}`}
                        onClick={() => handleStatus(slot.slotId, b, opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="klus-fields">
                    <label className="klus-field">
                      <span>Datum uitgevoerd</span>
                      <input
                        type="date"
                        value={b.uitvoerDatum || ''}
                        onChange={e => updateLocal(slot.slotId, b._id, { uitvoerDatum: e.target.value })}
                        onBlur={e => save(b._id, { uitvoerDatum: e.target.value })}
                      />
                    </label>
                    <label className="klus-field">
                      <span>Bijzonderheden</span>
                      <textarea
                        rows={2}
                        placeholder="Bijv. niet thuis, sleutel bij buren, schade gezien…"
                        value={b.uitvoerBijzonderheden || ''}
                        onChange={e => updateLocal(slot.slotId, b._id, { uitvoerBijzonderheden: e.target.value })}
                        onBlur={e => save(b._id, { uitvoerBijzonderheden: e.target.value })}
                      />
                    </label>
                  </div>
                </div>
              ))
            )}
          </div>
        ))}

        <div className="klus-footer">
          <p>Vragen? Mail <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default KlusPlanning;
